import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Lock, Gavel, ListChecks, Gauge, CalendarRange, Users, AlertTriangle,
  HelpCircle, Loader2, ShieldCheck, Clock, Target,
} from "lucide-react";
import BrandMark from "@/components/BrandMark";
import ThemeToggle from "@/components/ThemeToggle";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Panel = ({ title, icon: Icon, children, testid, lede }) => (
  <section data-testid={testid} className="yl-surface p-6 lg:p-8">
    <h2 className="font-heading text-xl sm:text-2xl flex items-center gap-3">
      <Icon className="w-5 h-5 text-primary shrink-0" strokeWidth={1.9} />
      {title}
    </h2>
    {lede && <p className="text-xs text-muted-foreground mt-2">{lede}</p>}
    <div className="mt-6">{children}</div>
  </section>
);

const STATUS_TONE = {
  Approved: "tone-sage", Implemented: "tone-sage", Rejected: "tone-blush",
  Done: "tone-sage", Completed: "tone-sage", Blocked: "tone-blush",
};

const Pill = ({ value, testid }) => (
  <span
    data-testid={testid}
    className={`${STATUS_TONE[value] || "tone-powder"} shrink-0 rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider`}
  >
    {value}
  </span>
);

function Gate({ onSubmit, busy, error }) {
  const [code, setCode] = useState("");
  return (
    <div className="min-h-screen grid place-items-center px-5 relative overflow-hidden bg-background">
      <div className="absolute inset-0 yl-mesh opacity-80" aria-hidden="true" />
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="yl-orb yl-orb-a opacity-30" />
        <div className="yl-orb yl-orb-b opacity-30" />
      </div>
      <form
        onSubmit={(e) => { e.preventDefault(); onSubmit(code); }}
        data-testid="recap-passcode-gate"
        className="relative yl-glass yl-beam rounded-3xl shadow-lift p-8 w-full max-w-sm yl-scale-in"
      >
        <BrandMark size="sm" />
        <div className="w-11 h-11 rounded-2xl grid place-items-center mt-7" style={{ background: "hsl(var(--cyan) / .14)" }}>
          <Lock className="w-5 h-5" style={{ color: "hsl(var(--ink-info))" }} strokeWidth={2} />
        </div>
        <h1 className="font-heading text-2xl mt-5">Protected recap</h1>
        <p className="text-sm text-muted-foreground mt-2.5 leading-relaxed">
          Enter the 4-digit passcode you were given to open this meeting recap.
        </p>
        <Input
          data-testid="recap-passcode-input"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
          inputMode="numeric"
          autoFocus
          placeholder="••••"
          className="mt-6 h-14 rounded-2xl text-center font-mono text-2xl tracking-[0.6em]"
        />
        {error && <p className="text-xs text-destructive mt-3" data-testid="recap-passcode-error">{error}</p>}
        <Button
          type="submit"
          data-testid="recap-passcode-submit"
          disabled={code.length !== 4 || busy}
          className="w-full h-12 rounded-full font-bold mt-5"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Open recap"}
        </Button>
      </form>
    </div>
  );
}

export default function PublicRecap() {
  const { token } = useParams();
  const [needsCode, setNeedsCode] = useState(null);
  const [data, setData] = useState(null);
  const [gone, setGone] = useState("");
  const [busy, setBusy] = useState(false);
  const [codeError, setCodeError] = useState("");

  const fetchRecap = useCallback(
    (passcode = "") => {
      setBusy(true);
      setCodeError("");
      axios
        .get(`${API}/public/recap/${token}`, { params: passcode ? { passcode } : {} })
        .then(({ data: d }) => setData(d))
        .catch((e) => {
          const status = e?.response?.status;
          if (status === 401) {
            setNeedsCode(true);
            if (passcode) setCodeError("That passcode is not correct.");
          } else if (status === 410) {
            setGone(e?.response?.data?.detail || "This recap link is no longer available.");
          } else {
            setGone("This recap could not be loaded.");
          }
        })
        .finally(() => setBusy(false));
    },
    [token]
  );

  useEffect(() => {
    axios
      .get(`${API}/public/recap/${token}/meta`)
      .then(({ data: d }) => {
        setNeedsCode(d.requires_passcode);
        if (!d.requires_passcode) fetchRecap();
      })
      .catch((e) => setGone(e?.response?.data?.detail || "This recap link is no longer available."));
  }, [token, fetchRecap]);

  if (gone) {
    return (
      <div className="min-h-screen grid place-items-center px-5 bg-background" data-testid="recap-unavailable">
        <div className="yl-surface yl-beam p-10 text-center max-w-md">
          <BrandMark size="sm" className="justify-center" />
          <ShieldCheck className="w-7 h-7 mx-auto mt-7 text-muted-foreground" strokeWidth={1.6} />
          <h1 className="font-heading text-2xl mt-5">Link unavailable</h1>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{gone}</p>
          <p className="text-xs text-muted-foreground mt-5">
            Ask the meeting organiser for a fresh link.
          </p>
        </div>
      </div>
    );
  }

  if (needsCode && !data) {
    return <Gate onSubmit={fetchRecap} busy={busy} error={codeError} />;
  }

  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-background" data-testid="recap-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const m = data.meeting || {};
  const vis = data.visibility || {};

  return (
    <div data-testid="public-recap" className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur-xl">
        <div className="mx-auto max-w-[1040px] px-5 sm:px-8 h-16 flex items-center gap-4">
          <BrandMark size="sm" showByline={false} testid="recap-brand" />
          <span className="ml-auto eyebrow text-[9px] hidden sm:inline">Read-only recap</span>
          <ThemeToggle testid="recap-theme-toggle" />
        </div>
      </header>

      {/* hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 yl-mesh opacity-70" aria-hidden="true" />
        <div className="relative mx-auto max-w-[1040px] px-5 sm:px-8 py-14 lg:py-20">
          <div className="eyebrow">{data.org?.name} · meeting recap</div>
          <h1 className="font-heading text-4xl sm:text-5xl mt-4 leading-[1.06]" data-testid="recap-title">
            {m.title}
          </h1>
          {m.purpose && (
            <p className="text-base md:text-lg text-muted-foreground mt-4 max-w-2xl leading-relaxed">{m.purpose}</p>
          )}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-8">
            <span className="inline-flex items-center gap-2 text-xs font-semibold text-muted-foreground">
              <CalendarRange className="w-3.5 h-3.5" strokeWidth={2.2} style={{ color: "hsl(var(--cyan))" }} />
              {m.date}{m.start ? ` · ${m.start}` : ""}{m.end ? `–${m.end}` : ""}
            </span>
            {m.duration_minutes ? (
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                <Clock className="w-3.5 h-3.5" strokeWidth={2.2} style={{ color: "hsl(var(--cyan))" }} />
                {Math.round(m.duration_minutes)} min
              </span>
            ) : null}
            {m.organizer && (
              <span className="inline-flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                <Users className="w-3.5 h-3.5" strokeWidth={2.2} style={{ color: "hsl(var(--cyan))" }} />
                Chaired by {m.organizer}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* counts */}
      <section className="border-b border-border bg-card/40">
        <div className="mx-auto max-w-[1040px] px-5 sm:px-8 py-8 grid grid-cols-2 sm:grid-cols-4 gap-6">
          {[
            { v: (data.decisions || []).length, l: "Decisions", show: vis.decisions !== false },
            { v: (data.actions || []).length, l: "Action items", show: vis.actions !== false },
            {
              v: data.on_agenda_percentage != null ? `${data.on_agenda_percentage}%` : "—",
              l: "On agenda", show: vis.coverage !== false,
            },
            { v: (data.participants || []).length, l: "Participants", show: vis.participants !== false },
          ].filter((x) => x.show).map((k) => (
            <div key={k.l} data-testid={`recap-stat-${k.l.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="font-heading text-3xl sm:text-4xl leading-none">{k.v}</div>
              <div className="eyebrow text-[9px] mt-2 text-muted-foreground">{k.l}</div>
            </div>
          ))}
        </div>
      </section>

      <main className="mx-auto max-w-[1040px] px-5 sm:px-8 py-12 space-y-6">
        {data.summary && (
          <Panel title="What was agreed" icon={Target} testid="recap-summary">
            <p className="text-base leading-relaxed text-foreground/90">{data.summary}</p>
          </Panel>
        )}

        {(data.agenda || []).length > 0 && (
          <Panel title="Agenda" icon={CalendarRange} testid="recap-agenda">
            <ol className="space-y-3">
              {(data.agenda || []).map((a, i) => (
                <li key={a} className="flex items-start gap-3.5">
                  <span className="font-mono text-[10px] text-muted-foreground mt-1 w-5 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-sm">{a}</span>
                </li>
              ))}
            </ol>
          </Panel>
        )}

        {(data.decisions || []).length > 0 && (
          <Panel title="Decisions" icon={Gavel} testid="recap-decisions">
            <ul className="divide-y divide-border">
              {data.decisions.map((d) => (
                <li key={d.ref} data-testid={`recap-decision-${d.ref}`} className="py-4 flex items-start gap-4">
                  <span className="font-mono text-[10px] text-muted-foreground mt-1 w-10 shrink-0">{d.ref}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold leading-snug">{d.decision}</span>
                    {d.owner && (
                      <span className="block text-xs text-muted-foreground mt-1">Decided by {d.owner}</span>
                    )}
                  </span>
                  <Pill value={d.status} />
                </li>
              ))}
            </ul>
          </Panel>
        )}

        {(data.actions || []).length > 0 && (
          <Panel title="Who does what next" icon={ListChecks} testid="recap-actions">
            <ul className="divide-y divide-border">
              {data.actions.map((a) => (
                <li key={a.ref} data-testid={`recap-action-${a.ref}`} className="py-4 flex items-start gap-4">
                  <span className="font-mono text-[10px] text-muted-foreground mt-1 w-10 shrink-0">{a.ref}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold leading-snug">{a.action}</span>
                    <span className="block text-xs text-muted-foreground mt-1">
                      {a.owner}{a.due ? ` · due ${a.due}` : ""}
                    </span>
                  </span>
                  <Pill value={a.status} />
                </li>
              ))}
            </ul>
          </Panel>
        )}

        {(data.coverage || []).length > 0 && (
          <Panel
            title="How the time was spent"
            icon={Gauge}
            testid="recap-coverage"
            lede="Share of discussion time per agenda item."
          >
            <div className="space-y-4">
              {data.coverage.map((c) => (
                <div key={c.item} data-testid={`recap-coverage-${c.item}`}>
                  <div className="flex items-center justify-between gap-4 text-sm font-semibold">
                    <span className="min-w-0 truncate">{c.item}</span>
                    <span className="font-mono text-xs text-muted-foreground shrink-0">
                      {c.percentage != null ? `${c.percentage}%` : "—"}
                    </span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-secondary overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${c.percentage || 0}%`,
                        background: "linear-gradient(90deg, hsl(var(--cyan)), hsl(var(--navy)))",
                      }}
                    />
                  </div>
                  {c.highlights && (
                    <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{c.highlights}</p>
                  )}
                </div>
              ))}
            </div>
          </Panel>
        )}

        {(data.risks || []).length > 0 && (
          <Panel title="Risks raised" icon={AlertTriangle} testid="recap-risks">
            <ul className="space-y-3">
              {data.risks.map((r) => (
                <li key={r} className="text-sm leading-relaxed">{r}</li>
              ))}
            </ul>
          </Panel>
        )}

        {(data.questions || []).length > 0 && (
          <Panel title="Still open" icon={HelpCircle} testid="recap-questions">
            <ul className="divide-y divide-border">
              {data.questions.map((q) => (
                <li key={q.question} className="py-3 flex items-start gap-4">
                  <span className="flex-1 text-sm">{q.question}</span>
                  <Pill value={q.status} />
                </li>
              ))}
            </ul>
          </Panel>
        )}

        {(data.participants || []).length > 0 && (
          <Panel title="Who was there" icon={Users} testid="recap-participants">
            <div className="flex flex-wrap gap-2.5">
              {data.participants.map((p) => (
                <span
                  key={p.name}
                  className="inline-flex items-center gap-2.5 rounded-full border border-border bg-card px-3.5 py-2"
                >
                  <span
                    className="w-6 h-6 rounded-lg grid place-items-center text-[9px] font-bold text-white"
                    style={{ background: "linear-gradient(135deg, hsl(var(--navy)), hsl(var(--cyan)))" }}
                  >
                    {p.name.slice(0, 2).toUpperCase()}
                  </span>
                  <span className="text-xs font-semibold">{p.name}</span>
                </span>
              ))}
            </div>
          </Panel>
        )}

        {data.health && (
          <Panel title="Meeting health" icon={Gauge} testid="recap-health">
            <div className="grid grid-cols-3 gap-6">
              {[
                { v: data.health.score ?? "—", l: "Health score" },
                { v: data.health.on_agenda != null ? `${data.health.on_agenda}%` : "—", l: "On agenda" },
                { v: data.health.speakers ?? "—", l: "Speakers" },
              ].map((k) => (
                <div key={k.l}>
                  <div className="font-heading text-3xl leading-none">{k.v}</div>
                  <div className="eyebrow text-[9px] mt-2 text-muted-foreground">{k.l}</div>
                </div>
              ))}
            </div>
          </Panel>
        )}
      </main>

      <footer className="border-t border-border bg-card">
        <div className="mx-auto max-w-[1040px] px-5 sm:px-8 py-10">
          <BrandMark size="sm" />
          <p className="text-xs text-muted-foreground mt-5 max-w-xl leading-relaxed">
            This read-only recap was shared{data.shared_by ? ` by ${data.shared_by}` : ""} from MeetIQ360 and is
            confidential to {data.org?.name}. The raw recording, full transcript and attendee contact details are
            not included.
            {data.expires_at && ` This link expires on ${new Date(data.expires_at).toLocaleDateString()}.`}
          </p>
          <a
            href="/home"
            data-testid="recap-cta"
            className="inline-flex items-center gap-2 mt-6 text-xs font-bold text-primary hover:gap-3 transition-[gap]"
          >
            Minutes like this, automatically — see MeetIQ360
          </a>
        </div>
      </footer>
    </div>
  );
}
